import React, {
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Sheet,
  Stack,
  Typography,
} from "@mui/joy";
import {
  HierarchyConfig,
  HierarchyGraph,
  HierarchyNode,
  visibleHierarchyBranches,
  hierarchyRoots,
} from "./hierarchyGraph";

interface Props {
  graph: HierarchyGraph;
  config: HierarchyConfig;
  partial?: boolean;
  onOpen?: (node: HierarchyNode) => void;
  canOpen?: (node: HierarchyNode) => boolean;
  onExpand?: (node: HierarchyNode) => void;
  expandingKey?: string | null;
}

/** Presentation only: inputs contain permitted nodes, never unresolved foreign identities. */
export function CanvasHierarchyGraphView({
  graph,
  config,
  partial,
  onOpen,
  canOpen,
  onExpand,
  expandingKey,
}: Props) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef(new Map<string, HTMLDivElement>());
  const [hiddenKinds, setHiddenKinds] = useState<Set<string>>(new Set());
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const visibleGraph = useMemo(() => {
    const edges = graph.edges.filter((edge) => !hiddenKinds.has(edge.kind));
    const configuredRoots = Object.values(config.starting_items || {}).some(
      (values) => values.length,
    );
    return {
      ...graph,
      edges,
      roots: focusKey
        ? [focusKey]
        : hiddenKinds.size && !configuredRoots
          ? hierarchyRoots(graph.nodes, edges)
          : graph.roots,
    };
  }, [graph, config.starting_items, hiddenKinds, focusKey]);
  const branches = useMemo(
    () => visibleHierarchyBranches(visibleGraph, config.max_depth || 50),
    [visibleGraph, config.max_depth],
  );
  const markerId = `hierarchy-arrow-${useId().replace(/[^a-zA-Z0-9]/g, "")}`;
  const [expansion, setExpansion] = useState<Record<string, boolean>>({});
  const isExpanded = (branch: { key: string; depth: number }) =>
    expansion[branch.key] ?? branch.depth < (config.initial_depth || 2);
  const nodes = new Map(graph.nodes.map((node) => [node.key, node]));
  const visible = branches.filter((branch) =>
    branches.every(
      (ancestor) =>
        ancestor.depth >= branch.depth ||
        !branch.key.startsWith(`${ancestor.key}/`) ||
        isExpanded(ancestor),
    ),
  );
  const toggle = (branch: { key: string; depth: number }) =>
    setExpansion((previous) => ({
      ...previous,
      [branch.key]: !isExpanded(branch),
    }));
  const kinds = [...new Set(graph.edges.map((edge) => edge.kind))];
  const kindColor = (kind: string) =>
    config.relationship_styles?.[kind]?.color ||
    `var(--joy-palette-${["primary", "success", "warning", "neutral"][kinds.indexOf(kind) % 4]}-500)`;
  const grid = config.layout !== "list";
  const depth = Math.max(1, ...visible.map((branch) => branch.depth));
  // Spread each depth over a ring. Chord spacing keeps bounded cards apart;
  // deeper rings remain outside shallower ones, including forests/shared refs.
  const radialRings = new Map<number, { indexes: number[]; radius: number }>();
  let previousRadius = 0;
  for (const level of [...new Set(visible.map((branch) => branch.depth))].sort(
    (a, b) => a - b,
  )) {
    const indexes = visible.flatMap((branch, index) =>
      branch.depth === level ? [index] : [],
    );
    const radius =
      level === 1 && indexes.length === 1
        ? 0
        : Math.max(
            previousRadius + 350,
            indexes.length > 1
              ? 350 / (2 * Math.sin(Math.PI / indexes.length))
              : 350,
          );
    radialRings.set(level, { indexes, radius });
    previousRadius = radius;
  }
  const radialSize = previousRadius * 2 + 260;
  const width =
    config.layout === "radial"
      ? radialSize
      : (config.layout === "flow" ? depth : visible.length) * 240;
  const height =
    config.layout === "radial"
      ? radialSize
      : (config.layout === "flow" ? visible.length : depth) * 240;
  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (config.layout !== "radial" || !viewport) return;
    viewport.scrollLeft = Math.max(0, (width - viewport.clientWidth) / 2);
    viewport.scrollTop = Math.max(0, (height - viewport.clientHeight) / 2);
  }, [config.layout, width, height]);
  const position = (index: number) => {
    if (config.layout === "radial") {
      const ring = radialRings.get(visible[index].depth)!;
      const angle =
        (ring.indexes.indexOf(index) / ring.indexes.length) * Math.PI * 2 -
        Math.PI / 2;
      return {
        x: Math.round(radialSize / 2 + ring.radius * Math.cos(angle) - 110),
        y: Math.round(radialSize / 2 + ring.radius * Math.sin(angle) - 110),
      };
    }
    return config.layout === "flow"
      ? { x: (visible[index].depth - 1) * 240, y: index * 240 }
      : { x: index * 240, y: (visible[index].depth - 1) * 240 };
  };

  return (
    <Box
      aria-label="Hierarchy"
      data-layout={config.layout}
      sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
    >
      {partial && (
        <Typography role="status">
          Partial hierarchy — load remaining rows before treating these as
          complete roots.
        </Typography>
      )}
      <Typography component="div" aria-label="Relationship kinds">
        {kinds.length
          ? kinds.map((kind) => (
              <Checkbox
                key={kind}
                label={kind}
                checked={!hiddenKinds.has(kind)}
                onChange={(event) =>
                  setHiddenKinds((previous) => {
                    const next = new Set(previous);
                    if (event.target.checked) next.delete(kind);
                    else next.add(kind);
                    return next;
                  })
                }
                sx={{ mr: 2, color: kindColor(kind) }}
              />
            ))
          : "No visible relationships"}
      </Typography>
      {focusKey && (
        <Button
          onClick={() => {
            setFocusKey(null);
            setExpansion({});
          }}
        >
          Return to starting items
        </Button>
      )}
      {graph.missingIdentityCount > 0 && (
        <Typography role="status">
          Items without a projected key were omitted.
        </Typography>
      )}
      {!visible.length && (
        <Typography>
          {graph.nodes.length
            ? "No visible starting items"
            : "No visible items"}
        </Typography>
      )}
      <Box
        ref={viewportRef}
        role="region"
        aria-label="Hierarchy items"
        sx={{ overflow: "auto", flex: 1, minHeight: 0 }}
      >
        <Box
          sx={
            grid
              ? {
                  position: "relative",
                  width: width,
                  minHeight: height,
                }
              : { display: "flex", flexDirection: "column", gap: 1 }
          }
        >
          {grid && (
            <svg
              aria-hidden="true"
              width={width}
              height={height}
              style={{ position: "absolute", inset: 0, pointerEvents: "none" }}
            >
              <defs>
                <marker
                  id={markerId}
                  markerWidth="8"
                  markerHeight="8"
                  refX="7"
                  refY="4"
                  orient="auto"
                >
                  <path d="M0,0 L8,4 L0,8 Z" fill="context-stroke" />
                </marker>
              </defs>
              {visibleGraph.edges.map((edge) => {
                const fromIndex = visible.findIndex(
                  (branch) => branch.nodeKey === edge.from,
                );
                const toIndex = visible.findIndex(
                  (branch, index) =>
                    branch.nodeKey === edge.to &&
                    (edge.from !== edge.to || index !== fromIndex),
                );
                if (fromIndex < 0 || toIndex < 0) return null;
                const from = position(fromIndex),
                  to = position(toIndex);
                const radial = config.layout === "radial";
                // End arrows at card boundaries so radial connections stay visible.
                const dx = to.x - from.x,
                  dy = to.y - from.y;
                const boundary = Math.max(
                  Math.abs(dx) / 110,
                  Math.abs(dy) / 100,
                  1,
                );
                const x1 = radial
                  ? from.x + 110 + dx / boundary
                  : from.x + (config.layout === "flow" ? 220 : 110);
                const y1 = radial
                  ? from.y + 110 + dy / boundary
                  : from.y + (config.layout === "flow" ? 110 : 210);
                const x2 = radial
                  ? to.x + 110 - dx / boundary
                  : to.x + (config.layout === "flow" ? 0 : 110);
                const y2 = radial
                  ? to.y + 110 - dy / boundary
                  : to.y + (config.layout === "flow" ? 110 : 10);
                return (
                  <g key={edge.key}>
                    <title>
                      {edge.kind}
                      {edge.label ? `: ${edge.label}` : ""}
                    </title>
                    <line
                      x1={x1}
                      y1={y1}
                      x2={x2}
                      y2={y2}
                      stroke={kindColor(edge.kind)}
                      strokeDasharray={
                        config.relationship_styles?.[edge.kind]?.dashed
                          ? "6 4"
                          : undefined
                      }
                      markerEnd={`url(#${markerId})`}
                    />
                    <text
                      x={(x1 + x2) / 2}
                      y={(y1 + y2) / 2 - 5}
                      fill="currentColor"
                      fontSize="12"
                    >
                      {edge.label || edge.kind}
                    </text>
                  </g>
                );
              })}
            </svg>
          )}
          {visible.map((branch, index) => {
            const node = nodes.get(branch.nodeKey)!;
            const hasChildren = visibleGraph.edges.some(
              (edge) => edge.from === node.key,
            );
            const canExpand =
              (hasChildren || (partial && !!onExpand)) &&
              !branch.cycle &&
              !visible
                .slice(0, index)
                .some((previous) => previous.nodeKey === branch.nodeKey) &&
              !branch.depthLimited;
            const at = position(index);
            return (
              <Sheet
                key={branch.key}
                variant="outlined"
                role="group"
                aria-label={`Item ${node.label}`}
                tabIndex={0}
                ref={(element: HTMLDivElement | null) => {
                  if (element) itemRefs.current.set(branch.key, element);
                  else itemRefs.current.delete(branch.key);
                }}
                onKeyDown={(event) => {
                  if (
                    ["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)
                  ) {
                    event.preventDefault();
                    event.stopPropagation();
                    const next =
                      event.key === "Home"
                        ? 0
                        : event.key === "End"
                          ? visible.length - 1
                          : event.key === "ArrowDown"
                            ? Math.min(visible.length - 1, index + 1)
                            : Math.max(0, index - 1);
                    itemRefs.current.get(visible[next].key)?.focus();
                    return;
                  }

                  if (
                    expandingKey ||
                    !canExpand ||
                    !["ArrowRight", "ArrowLeft"].includes(event.key)
                  )
                    return;
                  event.preventDefault();
                  event.stopPropagation();
                  const open = event.key === "ArrowRight";
                  setExpansion((previous) => ({
                    ...previous,
                    [branch.key]: open,
                  }));
                  if (open) onExpand?.(node);
                }}
                sx={{
                  p: 1,
                  borderRadius: "sm",
                  borderLeftWidth: node.status ? 4 : 1,
                  borderLeftColor:
                    config.nodes.find(
                      (mapping) => mapping.data_source_alias === node.alias,
                    )?.status_colors?.[node.status] || "divider",
                  ...(grid
                    ? {
                        position: "absolute",
                        left: at.x,
                        top: at.y + 10,
                        width: 220,
                        height: 200,
                        overflow: "auto",
                        boxSizing: "border-box",
                      }
                    : { ml: (branch.depth - 1) * 3 }),
                }}
              >
                {node.avatar && /^https?:\/\//i.test(node.avatar) && (
                  <Avatar src={node.avatar} alt={node.label} size="sm" />
                )}
                {node.icon && (
                  <Typography aria-label={`${node.label} icon`}>
                    {node.icon}
                  </Typography>
                )}
                {config.layout === "list" &&
                  visibleGraph.edges
                    .filter((edge) => edge.to === node.key)
                    .map((edge) => (
                      <Typography
                        key={edge.key}
                        level="body-xs"
                        sx={{ color: kindColor(edge.kind) }}
                      >
                        {edge.kind}
                        {edge.label ? `: ${edge.label}` : ""}
                      </Typography>
                    ))}
                {onOpen && (!canOpen || canOpen(node)) ? (
                  <Button
                    variant="plain"
                    aria-label={`Open ${node.label}`}
                    onClick={() => onOpen(node)}
                  >
                    {node.label}
                  </Button>
                ) : (
                  <Typography>{node.label}</Typography>
                )}
                {node.subtitle && (
                  <Typography level="body-sm">{node.subtitle}</Typography>
                )}
                {node.status && (
                  <Typography level="body-sm">{node.status}</Typography>
                )}
                {node.parentUnavailable && (
                  <Typography level="body-sm">Parent unavailable</Typography>
                )}
                {branch.cycle && (
                  <Typography level="body-sm">Cycle reference</Typography>
                )}
                {branch.shared && !branch.cycle && (
                  <Typography level="body-sm">Shared reference</Typography>
                )}
                {branch.depthLimited && (
                  <Stack>
                    <Typography level="body-sm">Depth limit reached</Typography>
                    {[
                      ...new Set(
                        visibleGraph.edges
                          .filter((edge) => edge.from === node.key)
                          .map((edge) => edge.to),
                      ),
                    ].map((key) => (
                      <Button
                        key={key}
                        onClick={() => {
                          setFocusKey(key);
                          setExpansion({});
                        }}
                      >
                        Continue from {nodes.get(key)?.label}
                      </Button>
                    ))}
                  </Stack>
                )}
                {canExpand && (
                  <Button
                    size="sm"
                    variant="plain"
                    aria-expanded={isExpanded(branch)}
                    aria-label={`${!hasChildren && partial && onExpand ? "Load children" : isExpanded(branch) ? "Collapse" : "Expand"} ${node.label}`}
                    disabled={!!expandingKey}
                    onClick={() => {
                      if (!hasChildren && partial && onExpand) {
                        setExpansion((previous) => ({
                          ...previous,
                          [branch.key]: true,
                        }));
                        onExpand(node);
                      } else {
                        if (!isExpanded(branch)) onExpand?.(node);
                        toggle(branch);
                      }
                    }}
                  >
                    {isExpanded(branch) ? "Collapse" : "Expand"}
                  </Button>
                )}
              </Sheet>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}
